import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Tag, Input, Select, Space, Card, Typography, Modal, Form, message,
  Popconfirm, Tooltip,
} from 'antd';
import { Plus, Search, Pencil, Trash2, FileText } from 'lucide-react';
import { blogApi } from '../../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;

const statusColors: Record<string, string> = {
  DRAFT: 'default',
  PUBLISHED: 'green',
  ARCHIVED: 'orange',
};

const statusLabels: Record<string, string> = {
  DRAFT: 'Borrador',
  PUBLISHED: 'Publicado',
  ARCHIVED: 'Archivado',
};

export default function BlogPostsPage() {
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<any>({});
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [category, setCategory] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const fetchPosts = () => {
    setLoading(true);
    blogApi.list({ page, limit: 20, search: search || undefined, status, category })
      .then((r) => { setPosts(r.data.data); setMeta(r.data.meta); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPosts(); }, [page, status, category]);

  const openCreate = () => {
    form.resetFields();
    setModalOpen(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const res = await blogApi.create(values);
      message.success('Artículo creado');
      setModalOpen(false);
      form.resetFields();
      navigate(`/blog/${res.data.id}`);
    } catch {
      message.error('Error al crear');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await blogApi.delete(id);
      message.success('Artículo eliminado');
      fetchPosts();
    } catch {
      message.error('Error al eliminar');
    }
  };

  const columns = [
    {
      title: '', key: 'cover', width: 60,
      render: (_: any, r: any) => r.coverImage
        ? <img src={r.coverImage} alt="" style={{ width: 48, height: 34, objectFit: 'cover', borderRadius: 4 }} />
        : <div style={{ width: 48, height: 34, background: '#f0f0f0', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FileText size={16} color="#999" /></div>,
    },
    { title: 'Título', dataIndex: 'title', key: 'title', ellipsis: true },
    {
      title: 'Estado', dataIndex: 'status', key: 'status',
      render: (s: string) => <Tag color={statusColors[s]}>{statusLabels[s] || s}</Tag>,
    },
    { title: 'Categoría', dataIndex: 'category', key: 'category', render: (c: string) => c || '-' },
    {
      title: 'Autor', dataIndex: 'author', key: 'author',
      render: (a: any) => a?.name || '-',
    },
    {
      title: 'Publicado', dataIndex: 'publishedAt', key: 'publishedAt',
      render: (d: string) => d ? dayjs(d).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Creado', dataIndex: 'createdAt', key: 'createdAt',
      render: (d: string) => dayjs(d).format('DD/MM/YYYY'),
    },
    {
      title: 'Acciones', key: 'actions', width: 120,
      render: (_: any, r: any) => (
        <Space size={4}>
          <Tooltip title="Editar">
            <Button type="link" size="small" icon={<Pencil size={14} />} onClick={(e) => { e.stopPropagation(); navigate(`/blog/${r.id}`); }} />
          </Tooltip>
          <Popconfirm title="¿Eliminar este artículo?" onConfirm={() => handleDelete(r.id)} okText="Eliminar" cancelText="Cancelar" okButtonProps={{ danger: true }}>
            <Tooltip title="Eliminar">
              <Button type="link" size="small" danger icon={<Trash2 size={14} />} onClick={(e) => e.stopPropagation()} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Blog</Title>
        <Button type="primary" icon={<Plus size={14} />} onClick={openCreate}>Nuevo Artículo</Button>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input placeholder="Buscar..." prefix={<Search size={14} />} value={search} onChange={(e) => setSearch(e.target.value)} onPressEnter={fetchPosts} style={{ width: 250 }} />
          <Select placeholder="Estado" allowClear style={{ width: 160 }} value={status} onChange={setStatus}
            options={Object.entries(statusLabels).map(([k, v]) => ({ value: k, label: v }))}
          />
          <Select placeholder="Categoría" allowClear style={{ width: 180 }} value={category} onChange={setCategory}
            options={['Mercado Inmobiliario', 'Guías', 'Lifestyle', 'Inversión', 'Noticias'].map(c => ({ value: c, label: c }))}
          />
        </Space>
      </Card>

      <Card>
        <Table columns={columns} dataSource={posts} loading={loading} rowKey="id" size="small" scroll={{ x: 900 }}
          onRow={(r) => ({ onClick: () => navigate(`/blog/${r.id}`), style: { cursor: 'pointer' } })}
          pagination={{ current: page, pageSize: 20, total: meta.total, onChange: setPage, showTotal: (t: number) => `${t} artículos` }}
        />
      </Card>

      <Modal title="Nuevo Artículo" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={() => form.submit()} okText="Crear" width={640} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="title" label="Título" rules={[{ required: true, message: 'El título es obligatorio' }]}>
            <Input placeholder="Título del artículo" />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="category" label="Categoría">
              <Select allowClear placeholder="Selecciona categoría"
                options={['Mercado Inmobiliario', 'Guías', 'Lifestyle', 'Inversión', 'Noticias'].map(c => ({ value: c, label: c }))}
              />
            </Form.Item>
            <Form.Item name="status" label="Estado" initialValue="DRAFT">
              <Select options={Object.entries(statusLabels).map(([k, v]) => ({ value: k, label: v }))} />
            </Form.Item>
          </div>
          <Form.Item name="excerpt" label="Extracto">
            <Input.TextArea rows={2} placeholder="Breve descripción del artículo..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
